<template>
  <v-card elevation="2" class="pb-1">
    <v-tabs
      v-model="selectedTab"
      show-arrows
      slider-size="4"
      slider-color="blue"
      class="sidebar-tabs"
    >
      <v-tab
        v-if="modelId && hasTasksActive && modelName !== 'challenges'"
        active-class="grey--text text--darken-4"
        key="tasks"
        :data-tourid="`${tourIdPrefix}-tab-tasks`"
        :disabled="isSingleTab"
        >{{ $t('tasks.task_other') }}</v-tab
      >
      <v-tab
        active-class="grey--text text--darken-4"
        v-if="modelId && hasCommentsActive"
        key="comments"
        :data-tourid="`${tourIdPrefix}-tab-comments`"
        :disabled="isSingleTab"
        >{{ $t('common.comment_other') }}</v-tab
      >
      <v-tab
        active-class="grey--text text--darken-4"
        v-if="modelId"
        key="about"
        :data-tourid="`${tourIdPrefix}-tab-about`"
        :disabled="isSingleTab"
        >{{ $t('common.about') }}</v-tab
      >
      <v-tab
        active-class="grey--text text--darken-4"
        v-if="modelId && hasAttachmentsActive"
        key="attachments"
        :data-tourid="`${tourIdPrefix}-tab-attachments`"
        :disabled="isSingleTab"
        >{{ $t('attachments.attachments') }}</v-tab
      >
      <v-tab-item
        eager
        active-class="tab-content hide-scrollbar"
        key="tasks"
        v-if="hasTasksActive && modelName !== 'challenges'"
      >
        <TasksTab
          :canEdit="canEdit"
          :rev="rev"
          :modelId="modelId"
          :siteId="siteId"
          :organizationId="organizationId"
        />
      </v-tab-item>

      <v-tab-item
        eager
        active-class="tab-content hide-scrollbar"
        key="comments"
      >
        <CommentsTab
          :canEdit="canEdit"
          :modelType="modelType"
          :modelName="modelName"
          :modelId="modelId"
          :rev="rev"
          :siteId="siteId"
          :submitCommentTestId="submitCommentTestId"
          :isEditMode="isEditMode"
          @notesChanged="notesChanged"
        />
      </v-tab-item>

      <v-tab-item eager active-class="tab-content-summary" key="about">
        <ChallengeSummary
          v-if="modelName === 'challenges' && modelId"
          :challenge="challenge"
        />
        <RevSummary
          v-if="isARevModel()"
          :rev="rev"
          :errors="errors"
          :isEditMode="isEditMode"
          :canEdit="canEdit"
          :hasChallengeActive="hasChallengeActive"
          :tags="tags"
          :modelType="modelType"
          :modelName="modelName"
          :modelId="modelId"
          @onLocationChange="onLocationChange"
          @onDueDateChange="onDueDateChange"
          @onChallengeChange="onChallengeChange"
          @priorityChanged="priorityChanged"
        />
      </v-tab-item>

      <v-tab-item
        eager
        active-class="tab-content hide-scrollbar"
        key="attachments"
      >
        <Attachments
          v-if="hasAttachmentsActive"
          :isEditMode="isEditMode"
          :revId="rev.id"
          :modelName="modelName"
        />
      </v-tab-item>
    </v-tabs>
  </v-card>
</template>
<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import constants from '@/constants'

import Attachments from '@/components/Core/Attachments/Attachments.vue'
import RevSummary from '@/components/SideBar/RevSummary.vue'
import CommentsTab from '@/components/Rev/CommentsTab.vue'
import TasksTab from '@/components/Rev/TasksTab.vue'
import ChallengeSummary from '@/components/Challenges/ChallengeSummary.vue'

const HIGHLIGHTED_COMMENT_QUERY_PARAM = 'highlightedComment'
export default {
  name: 'SideBar',

  components: {
    RevSummary,
    Attachments,
    CommentsTab,
    ChallengeSummary,
    TasksTab,
  },

  props: {
    modelType: {
      type: String,
      required: false,
    },
    modelName: {
      type: String,
      required: true,
    },
    modelId: {
      type: String,
      required: true,
      default: '',
    },
    submitText: {
      type: String,
      required: false,
    },
    submitCommentTestId: {
      type: String,
      required: false,
      default: 'comment_submit_btn',
    },
    canEdit: {
      type: Boolean,
      required: false,
    },
    isEditMode: {
      type: Boolean,
      required: false,
    },
    hasCommentsActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasAttachmentsActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    rev: {
      type: Object,
      required: false,
      default: function () {
        return {}
      },
    },
    errors: {
      type: Object,
      required: false,
      default: function () {
        return {}
      },
    },
    tags: {
      type: Array,
      required: false,
      default: function () {
        return []
      },
    },
    organizationId: {
      type: String,
      required: false,
      default: '',
    },
    hasChallengeActive: {
      type: Boolean,
      required: false,
    },
    challenge: {
      type: Object,
      required: false,
      default: function () {
        return {}
      },
    },
    tourIdPrefix: {
      type: String,
      default() {
        return 'rev-view'
      },
    },
  },

  watch: {
    isEditMode: {
      immediate: true,
      handler(editMode) {
        if (editMode && !this.hasTasksActive) {
          this.selectedTab = 1
        }
      },
    },
  },

  data() {
    return {
      sidebar_tabs: {
        sidebar__tabs: true,
      },
      sidebar_tab_tasks: {
        sidebar__tab: true,
        'sidebar__tab-tasks': true,
      },
      sidebar_tab_comments: {
        sidebar__tab: true,
        'sidebar__tab-comments': true,
      },
      sidebar_tab_about: {
        sidebar__tab: true,
        'sidebar__tab-about': true,
      },
      sidebar_tab_attachments: {
        sidebar__tab: true,
        'sidebar__tab-attachments': true,
      },

      hasAboutActive: true,
      selectedTab: this.getInitialActiveTab(),
    }
  },

  computed: {
    ...mapGetters(['organization']),
    isSingleTab() {
      const totalActiveTabs = _.compact([
        this.hasAttachmentsActive,
        this.hasCommentsActive,
        this.hasAboutActive,
        this.hasTasksActive,
      ]).length

      return totalActiveTabs === 1
    },

    siteId() {
      return (
        this.challenge?.sites?.[0]?._id ??
        this.rev?.location?.site?._id ??
        this.rev?.location?.site ??
        ''
      )
    },

    hasTasksActive() {
      return this.organization.featuresEnabled__tasks
    },
  },

  methods: {
    getInitialActiveTab() {
      const COMMENTS_TAB = 0
      const ABOUT_TAB = 1

      const isHighlightingComment = Boolean(
        this.$route?.query?.[HIGHLIGHTED_COMMENT_QUERY_PARAM]
      )
      const shouldSelectCommentsTab =
        this.modelName !== 'challenges' || isHighlightingComment

      if (shouldSelectCommentsTab && this.hasCommentsActive) {
        return COMMENTS_TAB
      }

      return ABOUT_TAB // For challenges, the initial tab is  "About" instead of "Comments"
    },

    onChallengeChange() {
      this.$emit('onChallengeChange')
    },

    onLocationChange(attrs) {
      this.$emit('onLocationChange', attrs)
    },
    onDueDateChange(date) {
      this.$emit('onDueDateChange', date)
    },
    notesChanged(notes) {
      this.$emit('notesChanged', notes)
    },
    isARevModel() {
      const revModels = _.values(constants.revModelTypes)
      return revModels.includes(this.modelName)
    },
    priorityChanged(priority) {
      this.$emit('priorityChanged', priority)
    },
  },
}
</script>

<style lang="scss">
.tab-content {
  height: 670px;
  overflow-y: scroll;
}
.tab-content-summary {
  min-height: 670px;
  overflow-y: visible;
}
.sidebar-tabs .v-item-group {
  overflow-y: visible;
}

.sidebar-tabs {
  .v-slide-group__prev,
  .v-slide-group__next {
    min-width: 40px !important;
  }
}
</style>
